'use client';

import { useRouter } from 'next/navigation';

import { Button, Separator } from '@mokoboko/components';
import { StickyNote } from 'lucide-react';

import { CalendarIntegration } from '@/components/calendar-integration';
import { Illustration } from '@/components/illustration';
import { useAuth } from '@/contexts/auth';
import { getCustomBookingPaymentDisplay } from '@/helpers/custom-class';
import { getTenantDirectionsUrl, getTenantPhoneHref } from '@/helpers/tenant';
import { useHasPassed } from '@/hooks/use-has-passed';
import { buildInstructorName } from '@/lib/utils';
import { CustomClassBooking } from '@/lib/types';
import { EMPTY_CALENDAR_ILLUSTRATION } from '@/lib/constants';
import useTenant from '@/state/tenant';

import {
  ReceiptDateRow,
  ReceiptLocationRow,
  ReceiptRow,
  ReceiptStatusHeader,
  type ReceiptStatus,
} from './receipt-rows';
import { StudioContactBox } from './studio-contact-box';

export const CustomClassReceipt = ({
  booking,
}: {
  booking: CustomClassBooking;
}) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { data: tenant } = useTenant();
  const { custom_class, instructor, booking_status, payment_summary } = booking;

  const studioName = booking.tenant?.name ?? 'the studio';
  const supportEmail = tenant?.support_email;
  const phoneHref = getTenantPhoneHref(tenant);

  const startDate = new Date(custom_class.start_time);
  const endDate = new Date(custom_class.end_time);

  const cancelled =
    custom_class.occurrence_status === 'CANCELLED' ||
    booking_status === 'CANCELLED';
  const paymentOnly = !cancelled && booking.viewer === 'PAYER';
  const classEnded = useHasPassed(endDate);
  const completed = !cancelled && !paymentOnly && classEnded;
  const status: ReceiptStatus = cancelled
    ? 'cancelled'
    : paymentOnly
      ? 'payment'
      : completed
        ? 'completed'
        : 'confirmed';

  const instructorName = instructor
    ? buildInstructorName(instructor.first_name, instructor.last_name)
    : null;

  const directionsUrl = custom_class.location
    ? getTenantDirectionsUrl(tenant, custom_class.location)
    : null;

  const payment = payment_summary
    ? getCustomBookingPaymentDisplay(payment_summary)
    : null;

  const dashboardButton = isAuthenticated ? (
    <Button
      size="lg"
      className="w-full justify-center"
      onClick={() => router.push('/dashboard')}
    >
      Go to dashboard
    </Button>
  ) : null;

  return (
    <div className="bg-white">
      <ReceiptStatusHeader
        status={status}
        title={custom_class.name}
        artwork={
          status === 'confirmed' ? (
            <Illustration
              name={EMPTY_CALENDAR_ILLUSTRATION}
              alt=""
              width={80}
              height={80}
              unoptimized
              className="h-20 w-20"
            />
          ) : undefined
        }
        label={status === 'confirmed' ? 'Custom class' : undefined}
      >
        {instructorName && (
          <p className="mt-1 text-sm text-gray-700">with {instructorName}</p>
        )}
      </ReceiptStatusHeader>

      <Separator className="my-6" />

      <div className="flex flex-col gap-5">
        <ReceiptDateRow
          startDate={startDate}
          endDate={endDate}
          cancelled={cancelled}
          completed={completed}
        />

        {custom_class.location && (
          <ReceiptLocationRow
            location={custom_class.location}
            directionsUrl={directionsUrl}
            cancelled={cancelled}
          />
        )}

        {payment && (
          <ReceiptRow
            icon={payment.icon}
            strikethrough={cancelled && payment_summary?.status !== 'REFUNDED'}
            title={payment.title}
          >
            {payment.detail && (
              <p className="mt-1 text-sm text-gray-600">{payment.detail}</p>
            )}
            {cancelled && payment_summary?.status === 'REFUNDED' && (
              <p className="mt-1 text-sm text-gray-600">
                Allow 5–10 business days for the refund to appear. Contact your
                bank with any questions.
              </p>
            )}
          </ReceiptRow>
        )}

        {custom_class.notes && (
          <ReceiptRow icon={StickyNote} title="Notes from the studio">
            <p className="mt-1 whitespace-pre-line text-sm text-gray-600">
              {custom_class.notes}
            </p>
          </ReceiptRow>
        )}
      </div>

      {status === 'confirmed' && (
        <>
          <Separator className="my-6" />
          <StudioContactBox
            heading="Manage booking"
            message={`Contact ${studioName} to reschedule or cancel this booking.`}
            supportEmail={supportEmail}
            phoneHref={phoneHref}
          />

          <div className="mt-5 flex w-full flex-col gap-2 sm:mt-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Add to calendar
            </p>
            <CalendarIntegration
              title={custom_class.name}
              startTime={startDate}
              endTime={endDate}
              location={custom_class.location}
              description={custom_class.notes ?? ''}
            />
          </div>
        </>
      )}

      {status === 'cancelled' && (
        <>
          <Separator className="my-6" />
          <div className="flex w-full flex-col gap-5">
            <StudioContactBox
              heading="Need help?"
              message={`Contact ${studioName} about this booking.`}
              supportEmail={supportEmail}
              phoneHref={phoneHref}
            />
            {dashboardButton}
          </div>
        </>
      )}

      {dashboardButton && (status === 'completed' || status === 'payment') && (
        <div className="mt-6">{dashboardButton}</div>
      )}
    </div>
  );
};
